import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Global navigation shortcuts.
 *
 * Single letters, so they must never fire while the user is typing — hence the
 * editable-target check, which also covers TipTap's contenteditable. Cmd-K
 * (palette) and `/` (quick capture) are owned by their own components and are
 * deliberately not duplicated here.
 */
const ROUTES: Record<string, string> = {
  t: '/',
  u: '/upcoming',
  i: '/inbox',
  a: '/all',
  b: '/board',
  v: '/table',
  s: '/search',
  r: '/review',
};

export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement ||
    el?.isContentEditable === true
  );
}

export function useShortcuts(): void {
  const navigate = useNavigate();

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Leave every modifier combination to the browser and to Cmd-K.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      const path = ROUTES[e.key.toLowerCase()];
      if (!path) return;

      e.preventDefault();
      navigate(path);
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);
}

/** Documented in the README. */
export const SHORTCUTS: { keys: string; does: string }[] = [
  { keys: 'Cmd/Ctrl K', does: 'Command palette' },
  { keys: '/', does: 'Focus quick capture' },
  { keys: 'T', does: 'Today' },
  { keys: 'U', does: 'Upcoming' },
  { keys: 'I', does: 'Inbox' },
  { keys: 'A', does: 'All tasks' },
  { keys: 'V', does: 'Table view' },
  { keys: 'B', does: 'Board' },
  { keys: 'S', does: 'Search' },
  { keys: 'R', does: 'Weekly review' },
  { keys: 'Esc', does: 'Close drawer or palette' },
];
